/**
 * The external-calendar sources list of Settings → Calendar (CR-31/33): the configured
 * import sources (CalDAV / Exchange / AD rooms / iCalendar link) with per-row Sync now /
 * Check / Edit / Delete actions, the create-edit dialog (SourceForm), the delete
 * confirmation and the compact sign-in hint for a guest. Every mutation reloads the list,
 * so the server-written sync state (lastSyncAt / lastSyncError) always shows fresh.
 */

import { useCallback, useEffect, useState } from 'react';
import type { JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';

import { Button } from '../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  CalendarApiError,
  checkSourceById,
  deleteSource,
  listSources,
  triggerSync,
  type CalendarSource,
  type SourceCheck,
} from './api';
import { CheckResultView, SourceForm, intervalLabel } from './SourceForm';
import { showToast, ToastStack } from './Toast';

/** Outcome of the row-level probe of one saved source (its stored credentials are used). */
type RowCheck =
  | { readonly status: 'checking' }
  | { readonly status: 'done'; readonly check: SourceCheck }
  | { readonly status: 'failed' };

/** Medium date-time of the last sync, in the UI language. */
function formatSyncAt(iso: string, locale: string): string {
  return new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeStyle: 'short' }).format(
    new Date(iso),
  );
}

export interface SourcesSectionProps {
  /** Bearer access token of the SSO session; null renders the compact sign-in hint. */
  readonly token: string | null;
}

export function SourcesSection({ token }: SourcesSectionProps): JSX.Element {
  const { t, i18n } = useTranslation();
  const locale = i18n.language === 'ru' ? 'ru-RU' : 'en-US';
  const [sources, setSources] = useState<readonly CalendarSource[] | null>(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<CalendarSource | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<CalendarSource | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [syncingId, setSyncingId] = useState<number | null>(null);
  const [rowChecks, setRowChecks] = useState<Readonly<Record<number, RowCheck>>>({});

  const load = useCallback(async (): Promise<void> => {
    if (token === null) {
      setSources(null);
      return;
    }
    try {
      setSources(await listSources(token));
      setLoadFailed(false);
    } catch {
      setLoadFailed(true);
    }
  }, [token]);

  // (Re)load whenever the session token appears or changes; a dead session surfaces as the
  // service-unavailable line - the api layer already rotated the refresh token once.
  useEffect(() => {
    setRowChecks({});
    setLoadFailed(false);
    void load();
  }, [load]);

  function openCreate(): void {
    setEditTarget(null);
    setFormOpen(true);
  }

  function openEdit(source: CalendarSource): void {
    setEditTarget(source);
    setFormOpen(true);
  }

  function closeForm(): void {
    setFormOpen(false);
    setEditTarget(null);
  }

  async function handleSync(source: CalendarSource): Promise<void> {
    if (token === null || syncingId !== null) {
      return;
    }
    setSyncingId(source.id);
    try {
      await triggerSync(token, source.id);
      showToast(
        'success',
        t('calendar.sources.sync_started_toast'),
        t('calendar.sources.sync_started_toast_desc'),
      );
    } catch (error: unknown) {
      if (error instanceof CalendarApiError && error.status === 409) {
        showToast('error', t('calendar.sources.sync_conflict_toast'));
      } else {
        showToast('error', t('calendar.sources.failed_toast'));
      }
    } finally {
      setSyncingId(null);
      void load();
    }
  }

  async function handleRowCheck(source: CalendarSource): Promise<void> {
    if (token === null) {
      return;
    }
    setRowChecks((current) => ({ ...current, [source.id]: { status: 'checking' } }));
    try {
      const check = await checkSourceById(token, source.id);
      setRowChecks((current) => ({ ...current, [source.id]: { status: 'done', check } }));
    } catch {
      setRowChecks((current) => ({ ...current, [source.id]: { status: 'failed' } }));
    }
  }

  async function handleDelete(): Promise<void> {
    if (token === null || deleteTarget === null || deleting) {
      return;
    }
    setDeleting(true);
    try {
      await deleteSource(token, deleteTarget.id);
      showToast('success', t('calendar.sources.deleted_toast'));
      setDeleteTarget(null);
      await load();
    } catch {
      showToast('error', t('calendar.sources.failed_toast'));
    } finally {
      setDeleting(false);
    }
  }

  if (token === null) {
    return (
      <div data-testid="sources-section" className="mt-4">
        <p
          data-testid="sources-signin-hint"
          className="rounded-lg border border-border bg-muted/40 p-3 text-[12.5px] text-muted-foreground"
        >
          {t('calendar.sources.sign_in_hint')}
        </p>
      </div>
    );
  }

  return (
    <div data-testid="sources-section" className="mt-4">
      <div className="flex items-center justify-between gap-3">
        <p className="text-[13px] font-medium text-foreground">{t('calendar.sources.title')}</p>
        <Button
          variant="outline"
          size="sm"
          className="h-8 shrink-0 gap-1.5 text-[12px]"
          data-testid="sources-add"
          onClick={openCreate}
        >
          {t('calendar.sources.add')}
        </Button>
      </div>

      {loadFailed ? (
        <p className="mt-3 text-[12px] text-red-400">{t('calendar.toasts.api_failed')}</p>
      ) : null}

      {sources !== null && sources.length === 0 && !loadFailed ? (
        <div className="mt-3 rounded-lg border border-dashed border-border p-4">
          <p className="text-[13px] font-medium text-foreground">{t('calendar.sources.empty')}</p>
          <p className="mt-0.5 text-[12px] text-muted-foreground">
            {t('calendar.sources.empty_hint')}
          </p>
        </div>
      ) : null}

      {sources !== null && sources.length > 0 ? (
        <div
          className="mt-2 divide-y divide-border border-t border-border/70"
          data-testid="sources-list"
        >
          {sources.map((source) => (
            <SourceRow
              key={source.id}
              source={source}
              locale={locale}
              check={rowChecks[source.id]}
              syncing={syncingId === source.id}
              onEdit={() => openEdit(source)}
              onSync={() => void handleSync(source)}
              onCheck={() => void handleRowCheck(source)}
              onDelete={() => setDeleteTarget(source)}
            />
          ))}
        </div>
      ) : null}

      <SourceForm
        open={formOpen}
        onOpenChange={(next) => (next ? setFormOpen(true) : closeForm())}
        token={token}
        source={editTarget}
        onSaved={() => void load()}
      />

      {/* delete confirmation: the source stops importing; imported rows stay read-only */}
      <Dialog
        open={deleteTarget !== null}
        onOpenChange={(next) => {
          if (!next) {
            setDeleteTarget(null);
          }
        }}
      >
        <DialogContent className="border-border bg-card p-0 sm:max-w-[420px]">
          <DialogHeader className="px-6 pb-1 pt-5">
            <DialogTitle className="text-[16px] font-semibold tracking-tight text-foreground">
              {t('calendar.sources.delete_confirm_title')}
            </DialogTitle>
            <DialogDescription className="text-[13px] leading-relaxed text-muted-foreground">
              {deleteTarget !== null
                ? t('calendar.sources.delete_confirm_body', { name: deleteTarget.name })
                : ''}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="px-6 pb-5 pt-3">
            <div className="flex w-full gap-2 sm:w-auto">
              <Button
                type="button"
                variant="outline"
                className="h-9 flex-1 border-border text-[13px] sm:flex-none"
                onClick={() => setDeleteTarget(null)}
              >
                {t('calendar.sources.cancel')}
              </Button>
              <Button
                type="button"
                variant="destructive"
                className="h-9 flex-1 gap-1.5 text-[13px] font-semibold sm:flex-none"
                data-testid="sources-confirm-delete"
                disabled={deleting}
                onClick={() => void handleDelete()}
              >
                {deleting ? <Loader2 aria-hidden className="size-3.5 animate-spin" /> : null}
                {t('calendar.sources.confirm_delete')}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ToastStack />
    </div>
  );
}

interface SourceRowProps {
  readonly source: CalendarSource;
  /** BCP-47 locale for the last-sync stamp (ru-RU / en-US). */
  readonly locale: string;
  readonly check: RowCheck | undefined;
  readonly syncing: boolean;
  readonly onEdit: () => void;
  readonly onSync: () => void;
  readonly onCheck: () => void;
  readonly onDelete: () => void;
}

function SourceRow({
  source,
  locale,
  check,
  syncing,
  onEdit,
  onSync,
  onCheck,
  onDelete,
}: SourceRowProps): JSX.Element {
  const { t } = useTranslation();
  return (
    <div className="py-3" data-testid={`source-row-${source.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <p className="truncate text-[13.5px] font-semibold text-foreground">{source.name}</p>
            <span className="rounded bg-muted px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
              {t(`calendar.sources.kind_${source.kind}`)}
            </span>
            <span
              className={
                source.enabled
                  ? 'flex items-center gap-1 text-[11px] text-emerald-400'
                  : 'flex items-center gap-1 text-[11px] text-muted-foreground'
              }
            >
              <span
                aria-hidden
                className={
                  source.enabled
                    ? 'size-1.5 rounded-full bg-emerald-400'
                    : 'size-1.5 rounded-full bg-muted-foreground/40'
                }
              />
              {source.enabled ? t('calendar.sources.enabled') : t('calendar.sources.disabled')}
            </span>
          </div>
          <p className="mt-0.5 text-[11.5px] text-muted-foreground">
            {intervalLabel(t, source.syncIntervalSecs)}
            {' · '}
            {t('calendar.sources.last_sync')}:{' '}
            {source.lastSyncAt !== null
              ? formatSyncAt(source.lastSyncAt, locale)
              : t('calendar.sources.never')}
          </p>
          {source.lastSyncError !== null ? (
            <p className="mt-0.5 text-[11.5px] text-red-400">{source.lastSyncError}</p>
          ) : null}
        </div>
        <div className="flex shrink-0 flex-wrap justify-end gap-1.5">
          <Button
            variant="outline"
            size="sm"
            className="h-7 px-2 text-[11.5px]"
            data-testid={`source-edit-${source.id}`}
            onClick={onEdit}
          >
            {t('calendar.sources.edit')}
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="h-7 gap-1 px-2 text-[11.5px]"
            data-testid={`source-sync-${source.id}`}
            disabled={syncing}
            onClick={onSync}
          >
            {syncing ? <Loader2 aria-hidden className="size-3 animate-spin" /> : null}
            {t('calendar.sources.sync_now')}
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="h-7 gap-1 px-2 text-[11.5px]"
            data-testid={`source-check-${source.id}`}
            disabled={check?.status === 'checking'}
            onClick={onCheck}
          >
            {check?.status === 'checking' ? (
              <Loader2 aria-hidden className="size-3 animate-spin" />
            ) : null}
            {t('calendar.sources.check')}
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="h-7 px-2 text-[11.5px] text-red-400 hover:text-red-300"
            data-testid={`source-delete-${source.id}`}
            onClick={onDelete}
          >
            {t('calendar.sources.delete')}
          </Button>
        </div>
      </div>
      {check !== undefined ? (
        <div className="mt-2" data-testid={`source-check-result-${source.id}`}>
          {check.status === 'checking' ? null : check.status === 'failed' ? (
            <p className="text-[12.5px] text-red-400">
              {t('calendar.sources.check_failed_generic')}
            </p>
          ) : (
            <CheckResultView check={check.check} kind={source.kind} />
          )}
        </div>
      ) : null}
    </div>
  );
}
